import { useMemo } from 'react';
import { IoClose } from 'react-icons/io5';
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner } from '@ds';
import { InfoBanner } from '@ui';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { getMultiStopCommercialString } from '@lp/features/RoutePlanner/utils';
import { CommercialFlightAviaLeg } from '@/api/apis/route';

export type RemoveConnectedLegsModalProps = {
  legs: CommercialFlightAviaLeg[];
  isOpen: boolean;
  isLoading: boolean;
  size?: 'md' | 'xs' | 'sm' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';
  onRemove: (sabreGroupId: string) => void;
  onClose: () => void;
};

export const RemoveConnectedLegsModal = ({
  legs,
  isOpen,
  isLoading,
  size = 'lg',
  onRemove,
  onClose,
}: RemoveConnectedLegsModalProps) => {
  const { list } = useRouteLocations();
  const multiStopListString = useMemo(() => {
    return getMultiStopCommercialString(legs, list);
  }, [legs, list]);

  const handleRemove = async () => {
    if (legs[0].sabreGroupId) {
      onRemove(legs[0].sabreGroupId);
    }
  };

  return (
    <Modal
      size={size}
      isOpen={isOpen}
      onClose={onClose}
      backdrop="opaque"
      closeButton={<IoClose size={45} style={{ top: 15 }} />}
      classNames={{
        base: 'px-6',
        body: 'border-y-1 border-base-30 px-0 py-6 text-l-r',
        header: 'px-0 text-base-20',
        footer: 'px-0 flex gap-6',
        wrapper: 'z-420',
        backdrop: 'z-419',
      }}
    >
      <ModalContent>
        {(handleClose) => (
          <>
            {isLoading && (
              <div className="absolute top-0 left-0 flex justify-center items-center z-50 w-full h-full bg-white bg-opacity-40">
                <Spinner size="md" />
              </div>
            )}
            <ModalHeader>
              <div className="flex flex-col">
                <span className="text-hm font-secondary">Delete Leg</span>
                <span className="text-s-r">Are you sure you want to delete this leg?</span>
              </div>
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-6 py-2">
                <span className="text-s-r text-base-20">
                  This leg is a part of multi-stop legs. So all connected legs will be deleted.
                </span>
                {!!multiStopListString && (
                  <InfoBanner iconType="multiStop">
                    <div className="flex gap-2 items-center">
                      <span className="text-m-r">Multi-stop legs.</span>
                      <span className="text-m-sb">{multiStopListString}</span>
                    </div>
                  </InfoBanner>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button size="md" variant="outline" onPress={handleClose}>
                Discard
              </Button>
              <Button isDisabled={isLoading} size="md" color="default" variant="solid" onPress={handleRemove}>
                Remove
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
